/**
 * What the person who asked sees while an agent works (ADR-0054): a short strip of plain steps
 * derived from the AG-UI view state, plus one headline sentence that says what is happening and
 * what happens next. Pure functions; the chat renders them with i18n keys under
 * `clarity:progress.*`.
 */
import { isShownMessage, type ApprovalView, type RunViewState, type WaitView } from "./agui";

export type ProgressKey = "rules" | "documents" | "approval" | "safety" | "answer" | "done";
export type ProgressStatus = "wait" | "active" | "done" | "error";

export interface ProgressStep {
  key: ProgressKey;
  status: ProgressStatus;
  /** i18n key (under `clarity:progress.step`) for the label in this state. */
  label: string;
  /** The approval behind an approval step (roles, approver). */
  approval?: ApprovalView;
  /** Values for the label (e.g. the number of pages read). */
  values?: Record<string, string | number>;
}

export type Headline =
  | { kind: "none" }
  | { kind: "checking" }
  | { kind: "waiting_approval"; approval: ApprovalView }
  /** A question or a choice for a person (an input task), not an approval. */
  | { kind: "waiting_input"; approval: ApprovalView }
  /** A "Wait for" step (ADR-0094): an event, a time, an answer or a return, and when it gives up. */
  | { kind: "waiting_event"; wait: WaitView }
  | { kind: "safety" }
  | { kind: "writing" }
  | { kind: "finished" }
  | { kind: "rejected"; approval: ApprovalView }
  | { kind: "blocked"; direction: "input" | "output" }
  | { kind: "failed"; reasons: string[] }
  | { kind: "cancelled" };

export interface RunProgress {
  steps: ProgressStep[];
  headline: Headline;
}

const rejected = (a: ApprovalView) => a.status === "decided" && a.decision === "reject";
const isQuestion = (a: ApprovalView) => a.kind === "human_input" || a.kind === "decision";

export function runProgress(s: RunViewState): RunProgress {
  if (s.status === "idle" && s.messages.length === 0) return { steps: [], headline: { kind: "none" } };
  const terminal = s.status === "finished" || s.status === "error" || s.status === "cancelled";
  const pending = s.approvals.find((a) => a.status !== "decided");
  const lastApproval = s.approvals[s.approvals.length - 1];
  const sawSafety = s.safety.input !== null || s.safety.output !== null;
  const blocked = s.safety.input === "block" ? "input" : s.safety.output === "block" ? "output" : null;
  // Only answers after the last approval request count as "the answer" (earlier ones explain why
  // an approval is needed).
  const assistant = s.messages.slice(lastApproval?.afterMessages ?? 0).filter((m) => m.role === "assistant" && isShownMessage(m));
  const streaming = assistant.some((m) => m.streaming);
  const wrote = assistant.length > 0;
  // A refusal before the run started (no run id) failed at the rules check.
  const refused = s.status === "error" && s.runId === null;
  const progressed = s.approvals.length > 0 || sawSafety || wrote || s.toolCalls.length > 0 || (terminal && !refused);

  const steps: ProgressStep[] = [];
  steps.push({
    key: "rules",
    status: refused ? "error" : progressed ? "done" : "active",
    label: progressed ? "rules_done" : "rules",
  });
  // Attached documents (ADR-0067): read by the agent, or not read and why.
  const read = s.documents.filter((d) => d.allowed);
  if (s.documents.length) {
    const refused = s.documents.length - read.length;
    const pages = read.reduce((n, d) => n + d.readPages, 0);
    steps.push({
      key: "documents",
      status: refused ? "error" : "done",
      label: refused ? (read.length ? "documents_some_refused" : "documents_refused") : read.length === 1 ? "document_read" : "documents_read",
      values: { count: read.length > 1 ? read.length : pages, refused, pages },
    });
  }
  if (lastApproval) {
    const a = pending ?? lastApproval;
    steps.push({
      key: "approval",
      status: pending ? "active" : rejected(a) ? "error" : "done",
      label: isQuestion(a) ? (pending ? "input_waiting" : "input_answered") : pending ? "approval_waiting" : rejected(a) ? "approval_rejected" : a.approver ? "approval_approved_by" : "approval_approved",
      approval: a,
    });
  }
  const afterApproval = !pending && !(lastApproval && rejected(lastApproval));
  // Shown when the safety checks reported, or while they may still come; never claimed when
  // the agent answered without them (no guardrails configured).
  if (sawSafety || (!terminal && afterApproval && !wrote)) {
    steps.push({
      key: "safety",
      status: blocked ? "error" : sawSafety ? "done" : progressed ? "active" : "wait",
      label: blocked ? "safety_blocked" : sawSafety ? "safety_done" : "safety",
    });
  }
  if (!(lastApproval && rejected(lastApproval)) || wrote) {
    // While a person still has to approve, the final answer is still to come.
    const answered = wrote && !pending;
    steps.push({
      key: "answer",
      status: streaming ? "active" : answered ? "done" : "wait",
      label: streaming ? "answer_writing" : answered ? "answer_done" : "answer",
    });
  }
  steps.push({
    key: "done",
    status: s.status === "finished" ? "done" : s.status === "error" || s.status === "cancelled" ? "error" : "wait",
    label: s.status === "finished" ? "done" : s.status === "cancelled" ? "cancelled" : s.status === "error" ? "failed" : "done_wait",
  });

  // Only one step is "active": the first one in order.
  let seenActive = false;
  for (const step of steps) {
    if (step.status === "active") {
      if (seenActive) step.status = "wait";
      seenActive = true;
    }
  }

  return { steps, headline: headline(s, pending, lastApproval, blocked, streaming) };
}

function headline(
  s: RunViewState,
  pending: ApprovalView | undefined,
  last: ApprovalView | undefined,
  blocked: "input" | "output" | null,
  streaming: boolean,
): Headline {
  if (s.status === "cancelled") return { kind: "cancelled" };
  if (s.status === "error") return { kind: "failed", reasons: s.errorReasons.length ? s.errorReasons : s.error ? [s.error] : [] };
  if (blocked && s.status === "finished") return { kind: "blocked", direction: blocked };
  if (last && rejected(last) && s.status === "finished") return { kind: "rejected", approval: last };
  if (s.status === "finished") return { kind: "finished" };
  if (s.wait) return { kind: "waiting_event", wait: s.wait };
  if (pending) return isQuestion(pending) ? { kind: "waiting_input", approval: pending } : { kind: "waiting_approval", approval: pending };
  if (streaming) return { kind: "writing" };
  if (last || s.safety.input !== null) return { kind: "safety" };
  if (s.status === "running" || s.status === "awaiting_approval") return { kind: "checking" };
  return { kind: "none" };
}
